import { Grid, Radio } from '@mui/material';
import { RoutalDialog, RoutalPalette } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { getPlanStatusColor } from '../constants/Colors';
import { PlanFlat } from '../domain/plan/PlanFlat';
import { PlanStatus } from '../domain/plan/PlanStatus';
import { useUpdateActivePlanStatus } from '../pages/planner/plan-details/hooks/plan-details/useUpdateActivePlanStatus';
import Button from '../react-components/Button';
import Text from '../react-components/Text';

const PlanStatusContainer = styled(Grid)<{ color: string }>`
  display: flex;
  height: 40px;
  width: 100%;
  align-items: center;
  border-radius: 5px;
  border: 1px solid ${({ color }) => color};
  margin-bottom: 12px;
  cursor: pointer;
`;

const PlanStatusOptions = [
  {
    color: getPlanStatusColor(`draft`, `dark`),
    status: `draft`,
  },
  {
    color: getPlanStatusColor(`planning`, `dark`),
    status: `planning`,
  },
  {
    color: getPlanStatusColor(`in_progress`, `dark`),
    status: `in_progress`,
  },
  {
    color: getPlanStatusColor(`finished`, `dark`),
    status: `finished`,
  },
] as { color: string; status: PlanStatus }[];

type PlanStatusDialogProps = {
  plans: PlanFlat[];
  dialogOpen: boolean;
  onUpdatePlanStatus: () => void;
  onCloseDialog: () => any;
};

export const PlanStatusDialog = ({ plans, dialogOpen, onUpdatePlanStatus, onCloseDialog }: PlanStatusDialogProps) => {
  const [updateStatus, setUpdateStatus] = useState<PlanStatus | undefined>(undefined);
  const { success: updateSuccess, isLoading: isUpdatingStatus, run: updatePlansStatus } = useUpdateActivePlanStatus();

  useEffect(() => {
    if (plans.length === 1 && plans[0].status) {
      setUpdateStatus(plans[0].status);
    }
  }, [plans]);

  useEffect(() => {
    if (updateSuccess) {
      onUpdatePlanStatus();
      onCloseDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSuccess]);

  const isSinglePlanAndStatusEquals = plans.length === 1 && plans[0].status === updateStatus;

  return (
    <RoutalDialog
      open={dialogOpen}
      onCloseDialog={onCloseDialog}
      titleContent={<Text variant="h6" intlId="update_plan_status_dialog.title" />}
      footerContent={
        <Button
          disabled={!updateStatus || isUpdatingStatus || isSinglePlanAndStatusEquals}
          id="change_status"
          styleType="primary"
          onClick={() => {
            if (updateStatus && !isUpdatingStatus && !isSinglePlanAndStatusEquals) {
              updatePlansStatus({ flatPlans: plans, status: updateStatus });
            }
          }}
          intlId="update_plan_status_dialog.button"
        />
      }
    >
      {PlanStatusOptions.map((option) => {
        const isSelected = updateStatus === option.status;
        return (
          <PlanStatusContainer
            color={isSelected ? option.color : RoutalPalette.neutral10}
            onClick={() => {
              setUpdateStatus(option.status);
            }}
          >
            <Radio
              checked={isSelected}
              onChange={() => {
                setUpdateStatus(option.status);
              }}
              style={{
                color: isSelected ? option.color : RoutalPalette.neutral10,
              }}
            />
            <Text
              style={{
                color: isSelected ? option.color : RoutalPalette.neutral40,
                fontWeight: isSelected ? `bold` : undefined,
                cursor: `pointer`,
              }}
              intlId={`${option.status}`}
            />
          </PlanStatusContainer>
        );
      })}
    </RoutalDialog>
  );
};
